import { FC, useCallback, useEffect, useMemo, useState } from "react";
import i18n from "i18n";
import { useNavigate } from "react-router";
import { UniqueIdentifier } from "@dnd-kit/core";
import { useQueryStages } from "Hooks/stages/useQueryStages";
import { useQueryLibraries } from "Hooks/stages/useQueryLibraries";
import { useQueryCreateStages } from "Hooks/stages/useQueryCreateStages";
import { useQueryDeleteStages } from "Hooks/stages/useQueryDeleteStages";
import { useQueryUpdateStages } from "Hooks/stages/useQueryUpdateStages";
import { useQueryOrderStages } from "Hooks/stages/useQueryOrderStages";
import { useQuerySettingsStages } from "Hooks/stages/useQuerySettingsStages";
import { useQueryImportLibrary } from "Hooks/stages/useQueryImportLibrary";
import Button from "Components/UI/Button";
import Item from "./components/Item";
import CreateOrEdit from "./components/CreateOrEdit";
import SimpleDnDListContext from "Components/UI/SimpleDnDListContext";
import SimpleSortableItemWithButton from "Components/UI/SimpleSortableItemWithButton";
import DeleteModal from "./components/Delete";
import Library from "./components/Library";
import EmptyList from "./components/EmptyList";
import PhaseNomenclatura from "./components/PhaseNomenclatura";
import IntegrationDiscipline from "./components/IntegrationDiscipline";
import DisciplineActivities from "./components/DisciplineActivities";
import LoadingStages from "Components/UI/CustomLoading/LoadingStages";
import SearchInput from "Components/UI/SearchInput";
import EmptySearch from "./components/EmptySearch";
import { Icon } from "Components/UI";
import { IConstructionSiteStageLibraryRequest } from "Data/interfaces/Obra/IObraLibraries";
import { VersionFieldEnum } from "Data/interfaces/Nomenclatura/INomenclatureData";
import {
  ICreateStageRequest,
  IDeleteStageRequest,
  IOrderStage,
  IOrderStageRequest,
  ISettingsStageRequest,
  IStage,
  IUpdateStageRequest,
} from "Data/interfaces/Obra/IObraStages";
import {
  headerStagesTable,
  InputStageRequest,
  SettingsConfigEnum,
  IStages,
} from "./stages.model";
import {
  BtnContainer,
  Container,
  Content,
  HeaderContent,
  Main,
  SwitchContainer,
  SwitchStyled,
  Title,
  Toolbar,
  Warning,
} from "./styles";

const Stages: FC<IStages> = ({
  csId,
  disciplines,
  loading,
  versionConfigFk,
  obraVersionData,
  ValidateStagesInNomenclature,
  AllowOnlyStagesLinkedToDisciplinesInUpload,
  AllowOnlyStagesLinkedToDisciplinesInTopics,
}) => {
  const navigate = useNavigate();

  const { data: stages, isFetching: listStagesLoading } = useQueryStages(csId);
  const { 
    data: libraries, 
    isFetching: listLibrariesLoading,
    refetch: refetchLibraries,
  } = useQueryLibraries();
  const {createStage} = useQueryCreateStages();
  const {updateStage} = useQueryUpdateStages();
  const {deleteStage} = useQueryDeleteStages();
  const {orderStage} = useQueryOrderStages();
  const {settingsStages} = useQuerySettingsStages();
  const {importLibrary} = useQueryImportLibrary();

  const [showEdit, setShowEdit] = useState(false);
  const [sentRequest, setSentRequest] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [stageForEdit, setStageForEdit] = useState<IStage | null>(null);
  const [phasesData, setPhasesData] = useState<IStage[]>([]);
  const [showPhaseNomenclaturaModal, setShowPhaseNomenclaturaModal] = useState(false);
  const [showIntegrationDisciplinesModal, setShowIntegrationDisciplinesModal] = useState(false);
  const [showDisciplineActivitiesModal, setShowDisciplineActivitiesModal] = useState(false);
  const noStageInNomenclatura = !obraVersionData?.VersionFields?.some(field => {
    return field.CampoVersaoId === VersionFieldEnum.Fase;
  })

  const activeData = useMemo(() => {
    if (activeId && phasesData?.length > 0) {
      const activeIndex = phasesData.findIndex(phase => {
        return phase.TopicStageId === Number(activeId);
      });
      if (activeIndex !== -1) {
        phasesData[activeIndex].tempIndex = activeIndex + 1;
        return phasesData[activeIndex];
      }
    }
    return null;
  }, [activeId, phasesData]);

  const filteredPhasesData = useMemo(() => {
    if (search && phasesData?.length > 0) {
      return phasesData.filter(phase => {
        const lowerCaseSearch = search.toLowerCase();
        return phase.Acronym?.toLowerCase().includes(lowerCaseSearch) ||
          phase.Name?.toLowerCase().includes(lowerCaseSearch) ||
          phase.TopicStageId?.toString()?.toLowerCase().includes(lowerCaseSearch);
      });
    }
    return phasesData;
  }, [phasesData, search]);

  const handleNomenclatureRedirect = () => {
    if (versionConfigFk) {
      return navigate(`/obras/edit/${csId}/nomenclature`);
    }
    return navigate('/config/nomenclatura');
  }

  const handleSubmit = (values: InputStageRequest) => {
    if (!showEdit || !values) return;

    if (stageForEdit) {
      const request: IUpdateStageRequest = {
        constructionStieId: csId,
        stageId: stageForEdit.TopicStageId,
        stage: {
          acronym: values.acronym,
          name: values.stageName,
          constructionSiteDisciplineIds: values.disciplineIds,
          isDefault: values.isDefault,
          order: stageForEdit.Order,
        },
      };
      updateStage(request);
    } else {
      const request: ICreateStageRequest = {
        constructionStieId: csId,
        stage: {
          acronym: values.acronym,
          name: values.stageName,
          constructionSiteDisciplineIds: values.disciplineIds,
          isDefault: values.isDefault,
        },
      };
      createStage(request);
    }
    setSentRequest(true);
  }

  const handleDelete = (stageId: number) => {
    if (!stageId) return;

    const request: IDeleteStageRequest = {
      constructionStieId: csId,
      stageId,
    };
    deleteStage(request);
    setSentRequest(true);
  }

  const handleImportSubmit = (values: IConstructionSiteStageLibraryRequest) => {
    if (!showLibrary || !values) return;

    importLibrary(values);
    setSentRequest(true);
  }

  const toogleSettingsConfig = (toggle: SettingsConfigEnum) => {
    const request: ISettingsStageRequest = {
      constructionStieId: csId,
      config: {
        validateStagesInNomenclature: ValidateStagesInNomenclature,
        allowOnlyStagesLinkedToDisciplinesInUpload: AllowOnlyStagesLinkedToDisciplinesInUpload,
        allowOnlyStagesLinkedToDisciplinesInTopics: AllowOnlyStagesLinkedToDisciplinesInTopics,
      }
    }

    if (toggle === SettingsConfigEnum.validateStagesInNomenclature) {
      request.config.validateStagesInNomenclature = !ValidateStagesInNomenclature;
      if (!request.config.validateStagesInNomenclature) {
        request.config.allowOnlyStagesLinkedToDisciplinesInUpload = false;
      }
    }
    if (toggle === SettingsConfigEnum.allowOnlyStagesLinkedToDisciplinesInUpload) {
      request.config.allowOnlyStagesLinkedToDisciplinesInUpload = !AllowOnlyStagesLinkedToDisciplinesInUpload;
    }
    if (toggle === SettingsConfigEnum.allowOnlyStagesLinkedToDisciplinesInTopics) {
      request.config.allowOnlyStagesLinkedToDisciplinesInTopics = !AllowOnlyStagesLinkedToDisciplinesInTopics;
    }

    settingsStages(request);
  }

  const openDeleteModal = (phase: IStage) => {
    setStageForEdit(phase);
    setShowDelete(true);
  }

  const openEditModal = (phase: IStage) => {
    setStageForEdit(phase);
    setShowEdit(true);
  }

  const openLibrary = () => {
    refetchLibraries();
    setShowLibrary(true);
  }

  const closeAllModal = () => {
    setSentRequest(false);
    setStageForEdit(null);
    setShowEdit(false);
    setShowPhaseNomenclaturaModal(false);
    setShowIntegrationDisciplinesModal(false);
    setShowDisciplineActivitiesModal(false);
    setShowDelete(false);
    setShowLibrary(false);
  }

  const handleSetNewData = useCallback((
    _start: IStage,
    _end: IStage,
    data: IStage[],
  ) => {
    if (data && data.length > 0) {
      const stagesData: IStage[] = [];
      const stagesForRequest: IOrderStage[] = [];
      for (const i in data) {
        const item = data[i];
        const order = Number(i) + 1;
        item.Order = order;
        stagesData.push(item);
        stagesForRequest.push({
          stageId: item.TopicStageId,
          order,
        })
      }
      const request: IOrderStageRequest = {
        constructionStieId: csId,
        stages: stagesData,
        stagesForRequest,
      };
      setPhasesData(data);
      orderStage(request);
    }
  }, [csId, orderStage]);

  useEffect(() => {
    if (stages.length > 0) {
      const orderedStages = stages.sort((a, b) => a.Order - b.Order);
      setPhasesData(orderedStages);
    }
  }, [stages]);

  useEffect(() => {
    if (sentRequest && !loading) {
      closeAllModal();
    }
  }, [sentRequest, loading]);

  return (
    <>
      <Container>
        <Title>Etapas</Title>
        <Main>
          <Content>
            {(listStagesLoading || phasesData?.length > 0) && (
              <>
                <Toolbar>
                  <div className="searchBox">
                    <SearchInput
                      setSearch={setSearch}
                      inputPlaceholder={i18n.t('editObra:disciplines.searchStagePlaceholder')}
                    />
                  </div>
                  <Button
                    type="text"
                    className="toolbarBtn"
                    disabled={loading || listStagesLoading}
                    onClick={() => setShowEdit(true)}
                  >
                    <Icon
                      icon="adicionar"
                      customSize={14}
                      className="actionIcon"
                    />
                    <span className="textBtn">{i18n.t('editObra:stagesPage.createStageTitle')}</span>
                  </Button>
                  {!listStagesLoading && (
                    <Button
                      type="text"
                      className="toolbarBtn"
                      onClick={openLibrary}
                    >
                      <Icon
                        icon="listDetailVision"
                        customSize={18}
                        className="actionIcon"
                      />
                      <span className="textBtn">{i18n.t('editObra:stagesPage.libraryButton')}</span>
                    </Button>
                  )}
                </Toolbar>
                <HeaderContent>
                  {headerStagesTable.map(head => (
                    <div key={`h-p-t-${head.id}`} className={`column${head.id}`}>
                      <span className="columnText">{head.label}</span>
                    </div>
                  ))}
                </HeaderContent>
                {listStagesLoading && (
                  <LoadingStages multiple={6} />
                )}
                {!listStagesLoading && filteredPhasesData?.length > 0 && (
                  <SimpleDnDListContext
                    data={filteredPhasesData.map(phase => ({ ...phase, id: phase.TopicStageId.toString() }))}
                    dragOverlay={activeId && activeData ? (
                      <SimpleSortableItemWithButton
                        key={activeData.TopicStageId}
                        id={activeData.TopicStageId.toString()}
                        activeId={activeId}
                        dragClassName="customDragRow"
                        btnClassName="customRowBtn"
                      >
                        <Item
                          key={`d-p-f-${activeData.TopicStageId}`}
                          index={activeData.tempIndex ? activeData.tempIndex : 1}
                          stage={activeData}
                          search={search}
                          disciplines={disciplines}
                          onUpdate={() => undefined}
                          onRemove={() => undefined}
                        />
                      </SimpleSortableItemWithButton>
                    ) : null}
                    onActiveId={setActiveId}
                    onSetNewData={handleSetNewData}
                  >
                    {filteredPhasesData.map((phase, index) => (
                      <SimpleSortableItemWithButton
                        key={phase.TopicStageId.toString()}
                        id={phase.TopicStageId.toString()}
                        className="customRow"
                        btnClassName="customRowBtn"
                        btnShowInHover
                        btnShowIsAbsolute
                      >
                        <Item
                          key={`d-p-f-${phase.TopicStageId}`}
                          index={index + 1}
                          stage={phase}
                          search={search}
                          disciplines={disciplines}
                          onUpdate={openEditModal}
                          onRemove={openDeleteModal}
                        />
                      </SimpleSortableItemWithButton>
                    ))}
                  </SimpleDnDListContext>
                )}
                {!listStagesLoading && search &&
                  filteredPhasesData?.length === 0 && (
                    <EmptySearch searchText={search} />
                  )}
              </>
            )}
            {!listStagesLoading && phasesData?.length === 0 && (
              <EmptyList
                onEdit={() => setShowEdit(true)}
                onLib={openLibrary}
              />
            )}
          </Content>
        </Main>
      </Container>

      <Container>
        <Title>Nomenclatura</Title>
        <Main>
          {noStageInNomenclatura && (
            <Warning>
              A nomenclatura não conta com o campo de Etapa e por isso, não é possível ativar a validação de etapas na nomenclatura.
            </Warning>
          )}
          {!noStageInNomenclatura && (!phasesData || phasesData?.length === 0) && (
            <Warning>
              Para configurar a validação das etapas na nomenclatura, é necessário ter cadastrada ao menos uma etapa.
            </Warning>
          )}
          {!noStageInNomenclatura && !!phasesData && phasesData?.some(phase => !phase?.Acronym) && (
            <Warning>
              Para configurar a validação das etapas na nomenclatura, é necessário que todas as Etapas tenham Sigla.
            </Warning>
          )}

          <SwitchContainer>
            <SwitchStyled
              checked={ValidateStagesInNomenclature}
              onChange={() => setShowPhaseNomenclaturaModal(true)}
              disabled={!phasesData ||
                phasesData?.length === 0 ||
                noStageInNomenclatura ||
                phasesData?.some(phase => !phase?.Acronym)
              }
            />
            <div className="textContent">
              <span className="title">
                Validar etapas na nomenclatura
              </span>
              <span className="text">
                Ao enviar um arquivo que tenha a extensão cadastrada na validação de nomenclatura, o campo de Etapa será validado com base nas etapas cadastradas. Não será possível escolher uma etapa que não esteja cadastrada.
              </span>
            </div>
          </SwitchContainer>
          <SwitchContainer>
            <SwitchStyled
              checked={AllowOnlyStagesLinkedToDisciplinesInUpload}
              onChange={() => ValidateStagesInNomenclature ? setShowIntegrationDisciplinesModal(true) : undefined}
              disabled={!ValidateStagesInNomenclature}
            />
            <div className="textContent">
              <span className="title">
                Permitir apenas as etapas vinculadas à disciplina no upload
              </span>
              <span className="text">
                Só será permitido selecionar as etapas que estão vinculadas com a disciplina que está sendo feito o upload.
              </span>
            </div>
          </SwitchContainer>
        </Main>

        <BtnContainer margintop={24}>
          <Button
            type="text"
            className="configBtn"
            onClick={handleNomenclatureRedirect}
          >
            <span className="textConfigBtn">Configurar Nomenclatura</span>
            <Icon
              icon="openNewTab"
              customSize={18}
              className="configIcon"
            />
          </Button>
        </BtnContainer>
      </Container>

      <Container>
        <Title>{i18n.t('editObra:stagesPage.activitiesSectionTitle')}</Title>
        <Main>
          {(!phasesData || phasesData?.length === 0) && (
            <Warning>
              {i18n.t('editObra:stagesPage.activitiesNoStageWarning')}
            </Warning>
          )}
          {!!phasesData && phasesData?.some(phase => !phase?.Acronym) && (
            <Warning>
              {i18n.t('editObra:stagesPage.activitiesNoAcronymWarning')}
            </Warning>
          )}

          <SwitchContainer>
            <SwitchStyled
              checked={AllowOnlyStagesLinkedToDisciplinesInTopics}
              onChange={() => setShowDisciplineActivitiesModal(true)}
              disabled={!phasesData ||
                phasesData?.length === 0 ||
                phasesData?.some(phase => !phase?.Acronym)
              }
            />
            <div className="textContent">
              <span className="title">
                {i18n.t('editObra:stagesPage.activitiesValidationTitle')}
              </span>
              <span className="text">
                {i18n.t('editObra:stagesPage.activitiesValidationDescription')}
              </span>
            </div>
          </SwitchContainer>
        </Main>
      </Container>

      {showEdit && (
        <CreateOrEdit
          visible={showEdit}
          editStage={stageForEdit}
          disciplines={disciplines}
          loading={loading}
          stages={stages}
          onSubmit={handleSubmit}
          onCancel={closeAllModal}
        />
      )}
      {showDelete && stageForEdit && (
        <DeleteModal
          visible={showDelete}
          name={stageForEdit.Name}
          stageId={stageForEdit.TopicStageId}
          isDefault={stageForEdit.IsDefault}
          loading={loading}
          onConfirm={handleDelete}
          onCancel={closeAllModal}
        />
      )}
      {showLibrary && (
        <Library
          visible={showLibrary}
          csId={csId}
          libraries={libraries}
          phases={phasesData}
          loading={loading}
          listLoading={listLibrariesLoading}
          onSubmit={handleImportSubmit}
          onCancel={closeAllModal}
        />
      )}
      {showPhaseNomenclaturaModal && (
        <PhaseNomenclatura
          visible={showPhaseNomenclaturaModal}
          active={ValidateStagesInNomenclature}
          loading={loading}
          onConfirm={() => toogleSettingsConfig(SettingsConfigEnum.validateStagesInNomenclature)}
          onCancel={closeAllModal}
        />
      )}
      {showIntegrationDisciplinesModal && (
        <IntegrationDiscipline
          visible={showIntegrationDisciplinesModal}
          active={AllowOnlyStagesLinkedToDisciplinesInUpload}
          onConfirm={() => toogleSettingsConfig(SettingsConfigEnum.allowOnlyStagesLinkedToDisciplinesInUpload)}
          onCancel={closeAllModal}
        />
      )}
      {showDisciplineActivitiesModal && (
        <DisciplineActivities
          visible={showDisciplineActivitiesModal}
          active={AllowOnlyStagesLinkedToDisciplinesInTopics}
          onConfirm={() => toogleSettingsConfig(SettingsConfigEnum.allowOnlyStagesLinkedToDisciplinesInTopics)}
          onCancel={closeAllModal}
        />
      )}
    </>
  )
}

export default Stages;
